import { Spacing } from '@persona-id/design-tokens';
import { noop } from 'lodash/fp';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import useIsITEInternal from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsITEInternal';
import {
  LOAD_VERSION_DIFF_ERROR_MESSAGE,
  PANEL_WIDTH,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/constants';
import InquiryTemplateJsonVersionDiff from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateJsonVersionDiff';
import { InquiryTemplateJsonVersionDiffProps } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateJsonVersionDiff/InquiryTemplateJsonVersionDiff';
import InquiryTemplateVisualVersionDiff from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVisualVersionDiff';
import { InquiryTemplateVisualVersionDiffProps } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVisualVersionDiff/InquiryTemplateVisualVersionDiff';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import NullState from 'dashboard/components-lib/NullState';
import { ProductFeatureName } from 'dashboard/models/organization';
import ToggleSlider from 'design-system/components/ToggleSlider';
import useProductFeature from 'lib/hooks/useProductFeature';

const InquiryTemplateVersionDiffContainer = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
`;

const InquiryTemplateVersionDiffToggleContainer = styled.div.attrs<{ visible: boolean }>(
  (props) => {
    return {
      style: {
        visibility: props.visible ? 'visible' : 'hidden',
      },
    };
  },
)<{ visible: boolean }>`
  display: flex;
  overflow: hidden;
  background-color: ${({ theme }) => theme.color.background.primary};
  border-top: 1px solid ${({ theme }) => theme.color.border.primary};
  border-right: 1px solid ${({ theme }) => theme.color.border.primary};
  width: ${PANEL_WIDTH}px;
  position: fixed;
  z-index: 1;
  bottom: 0;
  padding: ${Spacing[4]};
  gap: ${Spacing[1]};
`;

const NoContentContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

interface InquiryTemplateVersionDiffProps
  extends InquiryTemplateVisualVersionDiffProps,
    Pick<
      InquiryTemplateJsonVersionDiffProps,
      'inquiryTemplate' | 'currentSchema' | 'comparedVersion' | 'comparedSchema'
    > {
  handleLoadComparedVersion: () => void;
  handleLoadVersionDiff: () => void;
  loadingComparedVersion: boolean;
  loadingVersionDiff: boolean;
}

const InquiryTemplateVersionDiff = ({
  // Inquiry Template Json Version Diff Props
  inquiryTemplate,
  currentSchema,
  comparedSchema,
  comparedVersion,
  loadingComparedVersion,
  handleLoadComparedVersion,
  // Inquiry Template Version Diff View Props
  versionDiff,
  loadingVersionDiff,
  inquiryTemplateVersion,
  verificationTemplateVersionMap,
  workflowVersionMap,
  workflowSchema,
  stepsPreviewMap,
  previewPageMap,
  currentLocale,
  handleLoadVersionDiff,
}: InquiryTemplateVersionDiffProps) => {
  const isITEInternal = useIsITEInternal();
  const enhancedUsageBillingEnabled = useProductFeature(ProductFeatureName.InquiriesEnhanced);
  const [showJsonVersionDiff, setShowJsonVersionDiff] = useState(false);
  useEffect(() => {
    handleLoadVersionDiff();
    if (isITEInternal) {
      // Only load compared version if user is internal since only they have access to JSON version diff
      handleLoadComparedVersion();
    }
  }, [isITEInternal]);

  if (loadingVersionDiff) {
    return (
      <NoContentContainer>
        <LoadingIndicator />
      </NoContentContainer>
    );
  } else if (versionDiff == null) {
    return (
      <NoContentContainer>
        <NullState
          message={LOAD_VERSION_DIFF_ERROR_MESSAGE}
          backRoute={`/dashboard/inquiry-templates/${inquiryTemplate.id}/${inquiryTemplateVersion.id}/flow`}
        />
      </NoContentContainer>
    );
  }

  return (
    <InquiryTemplateVersionDiffContainer>
      <InquiryTemplateVersionDiffToggleContainer visible={isITEInternal}>
        <ToggleSlider
          labelOn='View as JSON'
          labelOff='View as JSON'
          value={showJsonVersionDiff}
          onChange={(value) => setShowJsonVersionDiff(value)}
        />
      </InquiryTemplateVersionDiffToggleContainer>
      {showJsonVersionDiff ? (
        <InquiryTemplateJsonVersionDiff
          inquiryTemplate={inquiryTemplate}
          currentVersion={inquiryTemplateVersion}
          currentSchema={currentSchema}
          comparedSchema={comparedSchema}
          comparedVersion={comparedVersion}
          enhancedUsageBillingEnabled={enhancedUsageBillingEnabled}
          loading={loadingComparedVersion}
          handleClose={noop}
        />
      ) : (
        <InquiryTemplateVisualVersionDiff
          versionDiff={versionDiff}
          inquiryTemplateVersion={inquiryTemplateVersion}
          verificationTemplateVersionMap={verificationTemplateVersionMap}
          workflowVersionMap={workflowVersionMap}
          workflowSchema={workflowSchema}
          stepsPreviewMap={stepsPreviewMap}
          previewPageMap={previewPageMap}
          currentLocale={currentLocale}
        />
      )}
    </InquiryTemplateVersionDiffContainer>
  );
};

export default InquiryTemplateVersionDiff;
